// Markdown-lite for card text (CardBlock.text is "paragraphs, links, emphasis. Never HTML."):
// paragraphs, "- " / "1. " lists, [label](https://…) and bare http(s) links, **bold**, *em*,
// `code`. The output is a small tree rendered with React elements, so nothing is ever parsed as
// HTML. Only http(s) links survive; anything else stays plain text.
// Inline grammar adapted from EveryInc/tend src/ui/FormattedText.tsx (MIT, (c) 2026 Every, Inc.).

export type Inline =
  | { t: "text"; v: string }
  | { t: "link"; v: string; href: string }
  | { t: "strong"; v: string }
  | { t: "em"; v: string }
  | { t: "code"; v: string }
  | { t: "br" };

export type Block =
  { t: "p"; inline: Inline[] } | { t: "ul"; items: Inline[][] } | { t: "ol"; items: Inline[][] };

export function safeHref(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

const TRAILING = /[.,;:!?'"]+$/;
const INLINE_RE =
  /(\[[^\]\n]+\]\([^)\s]+\)|https?:\/\/[^\s<>"'\]]+|`[^`\n]+`|\*\*[^*\s][^*\n]*\*\*|\*[^*\s][^*\n]*\*|\n)/g;

/** Drop trailing punctuation and an unbalanced closing parenthesis from a bare URL. */
function trimUrl(raw: string): string {
  let url = raw.replace(TRAILING, "");
  while (url.endsWith(")") && url.split("(").length < url.split(")").length) {
    url = url.slice(0, -1).replace(TRAILING, "");
  }
  return url;
}

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let cursor = 0;
  const push = (node: Inline) => {
    const last = out[out.length - 1];
    if (node.t === "text" && last?.t === "text") last.v += node.v;
    else out.push(node);
  };
  for (const match of text.matchAll(INLINE_RE)) {
    const start = match.index ?? 0;
    const raw = match[0];
    if (start > cursor) push({ t: "text", v: text.slice(cursor, start) });
    cursor = start + raw.length;
    if (raw === "\n") {
      out.push({ t: "br" });
      continue;
    }
    const link = raw.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]!);
      push(href ? { t: "link", v: link[1]!, href } : { t: "text", v: link[1]! });
      continue;
    }
    if (/^https?:\/\//i.test(raw)) {
      const trimmed = trimUrl(raw);
      const href = safeHref(trimmed);
      if (href) push({ t: "link", v: trimmed, href });
      else push({ t: "text", v: trimmed });
      if (trimmed.length < raw.length) push({ t: "text", v: raw.slice(trimmed.length) });
      continue;
    }
    if (raw.startsWith("`")) push({ t: "code", v: raw.slice(1, -1) });
    else if (raw.startsWith("**")) push({ t: "strong", v: raw.slice(2, -2) });
    else push({ t: "em", v: raw.slice(1, -1) });
  }
  if (cursor < text.length) push({ t: "text", v: text.slice(cursor) });
  return out;
}

const UL_RE = /^\s*[-*•]\s+/;
const OL_RE = /^\s*\d+[.)]\s+/;

export function parseBlocks(text: string | null | undefined): Block[] {
  const source = (text ?? "").replace(/\r\n?/g, "\n").trim();
  if (!source) return [];
  const blocks: Block[] = [];
  for (const chunk of source.split(/\n{2,}/)) {
    const lines = chunk.split("\n");
    if (lines.every((l) => UL_RE.test(l))) {
      blocks.push({ t: "ul", items: lines.map((l) => parseInline(l.replace(UL_RE, ""))) });
    } else if (lines.every((l) => OL_RE.test(l))) {
      blocks.push({ t: "ol", items: lines.map((l) => parseInline(l.replace(OL_RE, ""))) });
    } else {
      blocks.push({ t: "p", inline: parseInline(chunk) });
    }
  }
  return blocks;
}

/** Markdown-lite reduced to plain text (for one-line previews in lists). */
export function plainText(text: string | null | undefined): string {
  return parseInline((text ?? "").replace(/\s*\n+\s*/g, " "))
    .map((n) => (n.t === "br" ? " " : n.v))
    .join("");
}
